import React from "react";
import { Box, Container, Divider, List, ListItem, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { CheckCircle, MeetingRoom } from "@mui/icons-material";
import { useParams, Link } from "react-router-dom";
import dayjs from "dayjs";
import useResponsive from "../../hooks/useResponsive";

import InfoSection, { InformationField } from "../../components/general/InfoSection";
import EventChip from "../../components/events/EventChip";
import WorkshopInstanceEvaluationTable from "../../components/workshops/workshopEvaluation/WorkshopInstanceEvaluationTable";
import WorkshopInstanceEvaluationAccordions from "../../components/workshops/workshopEvaluation/WorkshopInstanceEvaluationAccordions";
import WorkshopInstanceTextEvaluation from "../../components/workshops/workshopEvaluation/WorkshopInstanceTextEvaluation";

import useWorkshopInstanceDetails from "../../hooks/workshops/useWorkshopInstanceDetails";
import { LoadingButton } from "@mui/lab";
import ConfirmationDialog from "../../components/general/ConfirmationDialog";

/**
 * This component is responsible for rendering the workshop instance evaluation page.
 * @returns the workshop instance evaluation page
 */
const WorkshopInstanceEvaluation: React.FunctionComponent = () => {
  const { workshopID, workshopInstanceID } = useParams();
  const { workshopInstanceFeedback, reopenFeedback, closeFeedback, isReopeningFeedback, isClosingFeedback } =
    useWorkshopInstanceDetails(Number(workshopID), Number(workshopInstanceID));

  const [isCloseFeedbackDialogOpen, setIsCloseFeedbackDialogOpen] = React.useState(false);
  const [isReopenFeedbackDialogOpen, setIsReopenFeedbackDialogOpen] = React.useState(false);

  const isMobile = useResponsive("down", "sm");

  // Handler for finishing the feedback phase, calls the closeFeedback function from the hook
  const handleFinishFeedback = () => {
    setIsCloseFeedbackDialogOpen(false);
    closeFeedback();
  };

  // Handler for reopening the feedback phase, calls the reopenFeedback function from the hook
  const handleReopenFeedback = () => {
    setIsReopenFeedbackDialogOpen(false);
    reopenFeedback();
  };

  const instanceFields: Array<InformationField> = [
    {
      label: "Durchschnitt",
      value: workshopInstanceFeedback?.calculatedGrade
        ? "Ø " + workshopInstanceFeedback.calculatedGrade.toFixed(2).toString()
        : null,
      type: "text",
    },
    {
      label: "Workshop",
      value: workshopInstanceFeedback?.workshopName ? workshopInstanceFeedback.workshopName : null,
      type: "text",
    },
    {
      label: "Status",
      value: workshopInstanceFeedback?.status ? workshopInstanceFeedback.status : null,
      type: "text",
    },
    {
      label: "Datum",
      value: workshopInstanceFeedback?.date ? dayjs(workshopInstanceFeedback.date).format("DD.MM.YYYY") : null,
      type: "text",
    },
    {
      label: "Startzeit",
      value: workshopInstanceFeedback?.startTime ? workshopInstanceFeedback.startTime.toString().slice(0, 5) : null,
      type: "text",
    },
    {
      label: "Endzeit",
      value: workshopInstanceFeedback?.endTime ? workshopInstanceFeedback.endTime.toString().slice(0, 5) : null,
      type: "text",
    },
    {
      label: "Ort",
      value: workshopInstanceFeedback?.location ? workshopInstanceFeedback.location : null,
      type: "text",
    },
    {
      label: "Referent",
      value: workshopInstanceFeedback?.referents
        ? workshopInstanceFeedback.referents.map((referent) => referent.firstname + " " + referent.lastname).join(", ")
        : null,
      type: "text",
    },
    {
      label: "Teilnehmeranzahl",
      value: workshopInstanceFeedback?.amountParticipants
        ? workshopInstanceFeedback.amountParticipants.toString()
        : null,
      type: "text",
    },
  ];

  return workshopInstanceFeedback === undefined ? (
    <Stack direction={"column"} spacing={2} sx={{ maxWidth: 800, ml: isMobile ? 0 : 5 }}>
      <Skeleton variant="rounded" sx={{ height: 50 }} />
      <Skeleton variant="rounded" sx={{ height: 500 }} />
    </Stack>
  ) : (
    <Container sx={{ maxWidth: 800, mb: 2 }}>
      <ConfirmationDialog
        open={isCloseFeedbackDialogOpen}
        onClose={() => {
          setIsCloseFeedbackDialogOpen(false);
        }}
        onConfirm={handleFinishFeedback}
        cofirmButtonColor="primary"
        title="Feedbackphase abschließen"
        content={
          "Möchtest du die Feedbackphase wirklich abschließen? Alle Teilnehmer, die noch kein Feedback abgegeben haben, können dies danach nicht mehr tun."
        }
        confirmButtonText={"Feedbackphase abschließen"}
      />
      <ConfirmationDialog
        open={isReopenFeedbackDialogOpen}
        onClose={() => {
          setIsReopenFeedbackDialogOpen(false);
        }}
        onConfirm={handleReopenFeedback}
        cofirmButtonColor="primary"
        title="Feedbackphase wieder öffnen"
        content={
          "Möchtest du die Feedbackphase wirklich wieder öffnen? Alle Teilnehmer, die noch kein Feedback abgegeben haben, können dies danach tun."
        }
        confirmButtonText={"Feedbackphase wieder öffnen"}
      />
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
        sx={{ mb: 1 }}
      >
        <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
          Workshop-Termin Feedback
        </Typography>
        {workshopInstanceFeedback.status === "Feedback" ? (
          <LoadingButton
            variant="contained"
            onClick={() => setIsCloseFeedbackDialogOpen(true)}
            sx={{ fontWeight: 600, fontSize: isMobile ? 10 : 14 }}
            color="success"
            startIcon={<CheckCircle />}
            disabled={workshopInstanceFeedback.membersWithoutFeedback.length > 0}
            loading={isClosingFeedback}
          >
            Feedbackphase abschließen
          </LoadingButton>
        ) : workshopInstanceFeedback.status === "Abgeschlossen" ? (
          <LoadingButton
            variant="contained"
            onClick={() => setIsReopenFeedbackDialogOpen(true)}
            sx={{ fontWeight: 600, fontSize: isMobile ? 10 : 14 }}
            color="info"
            startIcon={<MeetingRoom />}
            loading={isReopeningFeedback}
          >
            Feedbackphase wieder öffnen
          </LoadingButton>
        ) : null}
      </Stack>
      <Paper sx={{ pl: 3, pr: 3, pt: 1 }}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <Typography variant="h6" color="primary" fontWeight={"bold"}>
            Auswertung
          </Typography>
          <EventChip
            type={workshopInstanceFeedback.type ? workshopInstanceFeedback.type : "Sonstige"}
            sx={{ ml: 3 }}
            size="medium"
          />
        </Stack>

        <Box sx={{ mr: "auto", pt: 1, maxWidth: 800 }}>
          <InfoSection fields={instanceFields} />
        </Box>
        <Divider sx={{ width: "95%", borderColor: "#f6891f", mt: 2, mb: 2 }} />
        <Box sx={{ maxWidth: 1000 }}>
          <WorkshopInstanceEvaluationAccordions
            feedbackQuestionsEvaluation={workshopInstanceFeedback.feedbackQuestionsEvaluation}
            referentsEvaluation={workshopInstanceFeedback.referentsEvaluation}
          />
          <WorkshopInstanceTextEvaluation textFeedback={workshopInstanceFeedback.textFeedback} />
          <WorkshopInstanceEvaluationTable
            feedbackQuestionsEvaluation={workshopInstanceFeedback.feedbackQuestionsEvaluation}
            referentsEvaluation={workshopInstanceFeedback.referentsEvaluation}
            averageGrade={workshopInstanceFeedback.calculatedGrade}
          />
        </Box>
        {workshopInstanceFeedback.status === "Feedback" ? (
          <>
            <Divider sx={{ width: "95%", borderColor: "#f6891f", mt: 2, mb: 2 }} />
            <Box sx={{ maxWidth: 1000, pb: 3 }}>
              <Typography fontWeight={"bold"} fontSize={18}>
                Fehlendes Feedback
              </Typography>
              {workshopInstanceFeedback.membersWithoutFeedback.length > 0 ? (
                <Box>
                  <Typography>Die folgenden Teilnehmer haben noch kein Feedback abgegeben:</Typography>
                  <List dense sx={{ listStyle: "square", pl: 2 }}>
                    {workshopInstanceFeedback.membersWithoutFeedback.map((member) => {
                      return (
                        <ListItem key={member.memberId} sx={{ display: "list-item" }} disablePadding disableGutters>
                          <Link
                            to={`/gesamtuebersicht/${member.memberId}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            {member.firstName} {member.lastName}
                          </Link>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ) : (
                <Typography>Alle anwesenden Teilnehmer haben ihr Feedback abgegeben.</Typography>
              )}
            </Box>
          </>
        ) : workshopInstanceFeedback.membersWithoutFeedback.length > 0 ? (
          <>
            <Divider sx={{ width: "95%", borderColor: "#f6891f", mt: 2, mb: 2 }} />
            <Box sx={{ maxWidth: 1000, pb: 3 }}>
              <Typography fontWeight={"bold"} fontSize={18}>
                Fehlendes Feedback
              </Typography>
              <Box>
                <Typography>Die folgenden Teilnehmer haben noch kein Feedback abgegeben:</Typography>
                <List dense sx={{ listStyle: "square", pl: 2 }}>
                  {workshopInstanceFeedback.membersWithoutFeedback.map((member) => {
                    return (
                      <ListItem key={member.memberId} sx={{ display: "list-item" }} disablePadding disableGutters>
                        <Link
                          to={`/gesamtuebersicht/${member.memberId}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          {member.firstName} {member.lastName}
                        </Link>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </>
        ) : null}
      </Paper>
    </Container>
  );
};

export default WorkshopInstanceEvaluation;
